import React, { useEffect, useRef, useState } from "react";
import { Search, X, User, Handshake, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/api";

/**
 * Reusable Broker typeahead + inline "Create new broker" fallback.
 * value = broker doc (or null); onChange(broker) fires with picked/created broker or null.
 */
export const BrokerPicker = ({ value, onChange, testid = "broker-picker" }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await adminApi.listBrokers({ query: q, active: true });
        if (!cancelled) setRows(Array.isArray(data) ? data.slice(0, 10) : []);
      } catch { if (!cancelled) setRows([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (b) => { onChange(b); setQ(""); setRows([]); setOpen(false); };

  const createInline = async () => {
    if (!q.trim()) return;
    setCreating(true);
    try {
      const created = await adminApi.createBroker({ name: q.trim(), active: true });
      pick(created);
    } catch { /* silent — user can retry via full form */ }
    finally { setCreating(false); }
  };

  if (value) {
    return (
      <div className="flex items-center gap-2 border border-[#cfd7db] rounded-md p-2 bg-[#f5f6f6]" data-testid={`${testid}-selected`}>
        <Handshake size={14} className="text-[#f28a16]" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-[#111619] truncate">{value.name}</div>
          <div className="text-xs text-[#3f4b52] truncate">
            {[value.contact_person, value.phone, value.email].filter(Boolean).join(" · ") || "no contact info"}
          </div>
        </div>
        <button type="button" onClick={() => onChange(null)} className="text-[#3f4b52] hover:text-[#dc2626]" data-testid={`${testid}-clear`} aria-label="Clear broker">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-2 border border-[#cfd7db] rounded-md h-10 px-3 bg-white">
        <Search size={14} className="text-[#3f4b52] shrink-0" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search brokerage… (leave blank if none)"
          className="flex-1 h-full outline-none text-sm bg-transparent"
          data-testid={`${testid}-input`}
        />
      </div>
      {open && q.length >= 2 && (
        <div className="absolute z-40 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-[#cfd7db] rounded-md shadow-lg" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-[#3f4b52]">Searching…</div>}
          {!loading && rows.length === 0 && (
            <button
              type="button"
              onClick={createInline}
              disabled={creating}
              className="w-full text-left px-3 py-2 hover:bg-[#f5f6f6] flex items-center gap-2 border-b border-[#f5f6f6]"
              data-testid={`${testid}-create-new`}
            >
              {creating ? <Loader2 size={13} className="animate-spin text-[#f28a16]" /> : <User size={13} className="text-[#f28a16]" />}
              <span className="text-sm">
                <b>Create new broker</b> named &ldquo;{q}&rdquo;
              </span>
            </button>
          )}
          {rows.map((b) => (
            <button
              type="button" key={b.id}
              onClick={() => pick(b)}
              className="w-full text-left px-3 py-2 hover:bg-[#f5f6f6] flex items-center gap-2 border-b border-[#f5f6f6] last:border-b-0"
              data-testid={`${testid}-option-${b.id}`}
            >
              <Handshake size={13} className="text-[#f28a16] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-[#111619] truncate">{b.name}</div>
                <div className="text-xs text-[#3f4b52] truncate">
                  {[b.contact_person, b.phone, b.email].filter(Boolean).join(" · ") || "no contact info"}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BrokerPicker;
